import React, { useState, useRef, useLayoutEffect } from "react";
import Icon from "../../components/ui/icon";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/apiClient";
import "../../assets/styles/pages/publishers/campaigns.css";
import AdminSideNav from "../../components/layout/adminSideNav";
import DOMPurify from "dompurify";
import countryOptions from "../../utils/countryOptions";
import { convertCurrency } from "../../utils/currencyConverter";
import { storageManager } from "../../utils/storageManager";

const AdminCampaignsPage = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const [isSideNavActive, setisSideNavActive] = useState(false);
  const [userInfo, setUserInfo] = useState(
    JSON.parse(localStorage.getItem("AdminInfo")) || {}
  );
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsFetchStatus, setCampaignsFetchStatus] =
    useState("fetching_first");
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [userVerified, setUserVerified] = useState(false);
  const limit = 20;
  const [campaignSearchText, setCampaignSearchText] = useState("");
  const [isSearchingCamps, setIsSearchingCamps] = useState(false);
  const [isShowingSearchResults, setIsShowingSearchResults] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState({});
  const [getTrackingLinkWindow, setGetTrackingLinkWindow] = useState(null);
  const [gettingLink, setGettingLink] = useState(false);
  const [publishers, setPublishers] = useState(false);
  const [selectedPublisher, setSelectedPublisher] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState({});
  const [pubFetchStatus, setpubFetchStatus] = useState("fetching");
  const [pubEmailToSearch, setPubEmailToSearch] = useState("");
  const [prevSearched, setPrevSearched] = useState(false);
  const [searchStatus, setSearchStatus] = useState("idle");
  const [mailingCampInfo, setMailingCampInfo] = useState(false);
  const debouncePSearchRef = useRef(null);
  const isLoadingMore = useRef(false);
  const debounceRef = useRef(null);
  const isNotAdminUser = userInfo.role && userInfo.role !== "admin";
  const currency = storageManager.getCurrency();

  useLayoutEffect(() => {
    const token = localStorage.getItem("aToken");
    if (!token) {
      navigate("/auth/login");
      return;
    }

    const validateUser = async () => {
      if (userVerified) return;

      api
        .get(`/api/admin/info`)
        .then((response) => {
          setUserInfo(response.data.data);
          setUserVerified(true);
          localStorage.setItem("AdminInfo", JSON.stringify(response.data.data));
          fetchCampaigns();

          api
            .get(`/api/a/publisher/?limit=20&skip=0&status=active`)
            .then((response) => {
              setPublishers(response.data.data);
              setpubFetchStatus("fetched");
            })
            .catch((err) => {
              showToast({
                status: "error",
                message:
                  err?.response?.data?.message || "Error Fetching Publishers",
              });
            });
        })
        .catch((err) => {
          showToast({
            status: "error",
            message: err.response?.data?.message || "Login not found.",
          });
          navigate("/auth/login");
        });
    };

    const fetchCampaigns = () => {
      const token = localStorage.getItem("aToken");

      if (!token) {
        console.error("No auth token found");
        setCampaignsFetchStatus("failed");
        return;
      }

      if (campaignsFetchStatus === "fetching" || !hasMore) return;
      if (campaignsFetchStatus !== "fetching_first")
        setCampaignsFetchStatus("fetching");

      api
        .get(`/api/a/campaign?limit=${limit}&skip=${skip}`)
        .then((response) => {
          if (response.data.status) {
            const fetchedCampaigns = response.data.campaigns;

            if (fetchedCampaigns.length < limit) {
              setHasMore(false);
              setCampaignsFetchStatus("not_needed");
            }

            isLoadingMore.current = false;

            setCampaigns((prev) => [...prev, ...fetchedCampaigns]);
            setSkip((prevSkip) => prevSkip + limit);
            setCampaignsFetchStatus("success");
          } else {
            setCampaignsFetchStatus("failed");
          }
        })
        .catch((err) => {
          setCampaignsFetchStatus("failed");
        });
    };

    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = document.documentElement.scrollHeight - 300;

      if (scrollPosition >= threshold && hasMore) {
        if (campaignsFetchStatus === "fetching_first") return;
        if (isShowingSearchResults) return;
        if (isSearchingCamps) return;
        if (isLoadingMore.current) return;
        isLoadingMore.current = true;
        fetchCampaigns();
      }
    };

    validateUser();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [
    campaignsFetchStatus,
    hasMore,
    navigate,
    showToast,
    skip,
    userVerified,
    isShowingSearchResults,
    isSearchingCamps,
    isNotAdminUser
  ]);

  const sanitizeInput = (value) => DOMPurify.sanitize(value);

  const handelActionButton = (campaign) => {
    if (isNotAdminUser) return showToast({
      status: "error",
      message: "You are not authorized to perform this action."
    });
    navigate(`/admin/campaign/edit/${campaign.campaignId}`, { state: { campaign } });
  };

  const handelCampSearchInpChnage = (e) => {
    const sText = sanitizeInput(e.target.value);
    setCampaignSearchText(sText);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (sText.length === 0) {
      setCampaigns([]);
      setSkip(0);
      setHasMore(true);
      setIsSearchingCamps(false);
      setCampaignsFetchStatus("fetching_first");
      api
        .get(`/api/a/campaign?limit=${limit}&skip=0`)
        .then((response) => {
          if (response.data.status) {
            const fetchedCampaigns = response.data.campaigns;

            if (fetchedCampaigns.length < limit) {
              setHasMore(false);
              setCampaignsFetchStatus("not_needed");
            }

            setCampaigns((prev) => [...prev, ...fetchedCampaigns]);
            setSkip((prevSkip) => prevSkip + limit);
            setCampaignsFetchStatus("success");
            setIsShowingSearchResults(false);
          } else {
            setCampaignsFetchStatus("failed");
          }
        })
        .catch((err) => {
          console.error(err);
          setCampaignsFetchStatus("failed");
        });
    }

    if (sText.length < 3 && sText.length > 0) {
      setIsSearchingCamps(false);
      return;
    }

    if (sText.length >= 3) {
      setIsSearchingCamps(true);

      debounceRef.current = setTimeout(() => {
        api
          .get(`/api/a/campaign/search?searchText=${sText}&filter=all`)
          .then((response) => {
            if (response.data.status) {
              setCampaigns(response.data.campaigns);
              setIsShowingSearchResults(true);
            }
          })
          .catch((err) => {
            showToast({
              status: "error",
              message:
                err.response?.data?.message ||
                "Something went wrong while searching.",
            });
          })
          .finally(() => {
            setIsSearchingCamps(false);
          });
      }, 1500);
    }
  };

  const handleToggleStatus = async (campaign) => {
    if (isNotAdminUser) return showToast({
      status: "error",
      message: "You are not authorized to perform this action."
    });
    if (isUpdatingStatus[campaign.campaignId]) return;

    const newStatus = campaign.status === "active" ? "paused" : "active";
    setIsUpdatingStatus((prev) => ({ ...prev, [campaign.campaignId]: true }));

    try {
      const res = await api.patch("/api/a/campaign/update-status", {
        campaignId: campaign.campaignId,
        status: newStatus,
      });

      if (res.data.status) {
        setCampaigns((prev) =>
          prev.map((c) =>
            c.campaignId === campaign.campaignId
              ? { ...c, status: newStatus }
              : c
          )
        );
        showToast({
          status: "success",
          message: `Campaign status updated to ${newStatus}`,
        });
      } else {
        showToast({
          status: "error",
          message: res.data.message || "Failed to update status",
        });
      }
    } catch (err) {
      showToast({
        status: "error",
        message: err.response?.data?.message || "Failed to update campaign status",
      });
    } finally {
      setIsUpdatingStatus((prev) => ({
        ...prev,
        [campaign.campaignId]: false,
      }));
    }
  };

  const getLabel = (value) => {
    const item = countryOptions.find(c => c.value === value);
    return item ? `${item.flag} ${item.code}` : value;
  };

  const getFlag = (value) => {
    const item = countryOptions.find(c => c.value === value);
    return item ? `${item.flag}` : value;
  };

  const fetchPublishersOut = async (flimit, fskip) => {
    if (pubFetchStatus === "fetching") return;
    setpubFetchStatus("fetching");

    api
      .get(`/api/a/publisher/?limit=${flimit}&skip=${fskip}&status=active`)
      .then((response) => {
        setPublishers((prev) => [...prev, ...response.data.data]);
        setSkip(fskip + flimit);
        setpubFetchStatus("fetched");
      })
      .catch((err) => {
        showToast({
          status: "error",
          message:
            err?.response?.data?.message || "Error Fetching Publishers",
        });
      });
  };

  async function joinPublisherToCampaign(campaignId, publisherId) {
    if (gettingLink) return;
    setGettingLink(true);

    try {
      const res = await api.post("/api/a/publisher/approve-campaign", {
        campaignId,
        publisherId,
      });

      const data = res.data;

      if (!data.status) {
        showToast({
          status: "error",
          message: data.message || "Failed to join campaign",
        });
        setGettingLink(false);
        return false;
      }

      if (data.joined) {
        setPublishers((prev) =>
          prev.map((p) =>
            p.publisherId === publisherId
              ? {
                ...p,
                campaignsJoined: [...(p.campaignsJoined || []), selectedCampaign._id],
              }
              : p
          )
        );

        setSelectedPublisher((prev) => ({
          ...prev,
          campaignsJoined: [...(prev.campaignsJoined || []), selectedCampaign._id],
        }));

        showToast({
          status: "success",
          message: "Publisher successfully joined this campaign",
        });

        setGettingLink(false);
        return true;
      }

      showToast({
        status: "error",
        message: data.message || "Publisher is already joined",
      });

      setGettingLink(false);
      return false;
    } catch (error) {
      showToast({
        status: "error",
        message: error.response?.data?.message || "Internal error. Please try again.",
      });

      setGettingLink(false);
      return false;
    }
  }

  async function rejectPublisherFromCampaign(campaignId, publisherId) {
    if (gettingLink) return;
    setGettingLink(true);

    try {
      const res = await api.post("/api/a/publisher/reject-campaign", {
        campaignId,
        publisherId,
      });

      const data = res.data;

      if (!data.status) {
        showToast({
          status: "error",
          message: data.message || "Failed to reject publisher",
        });
        setGettingLink(false);
        return false;
      }

      if (data.rejected) {
        setPublishers((prev) =>
          prev.map((p) =>
            p.publisherId === publisherId
              ? {
                ...p,
                campaignsJoined: (p.campaignsJoined || []).filter(
                  (id) => id !== selectedCampaign._id
                ),
              }
              : p
          )
        );

        setSelectedPublisher((prev) => ({
          ...prev,
          campaignsJoined: (prev.campaignsJoined || []).filter(
            (id) => id !== selectedCampaign._id
          ),
        }));

        showToast({
          status: "success",
          message: "Publisher successfully rejected from campaign",
        });

        setGettingLink(false);
        return true;
      }

      showToast({
        status: "info",
        message: data.message || "Publisher was not joined to this campaign",
      });

      setGettingLink(false);
      return false;
    } catch (error) {
      showToast({
        status: "error",
        message:
          error.response?.data?.message ||
          "Internal error. Please try again.",
      });

      setGettingLink(false);
      return false;
    }
  }

  function handleSearchTyping(value) {
    setPubEmailToSearch(value);

    if (debouncePSearchRef.current) clearTimeout(debouncePSearchRef.current);
    debouncePSearchRef.current = setTimeout(() => {
      handleSearchPublisher(value);
    }, 500);
  }

  function handleSearchPublisher(value) {
    const searchTerm = sanitizeInput(value.trim());

    if (prevSearched && (searchTerm === "" || searchTerm === null)) {
      setPrevSearched(false);
      setPublishers([]);
      setpubFetchStatus("fetching_first");
      setSearchStatus("idle");
      fetchPublishersOut(20, 0);
      return;
    }
    if (searchStatus === "searching") return;
    if (searchTerm === "" && searchTerm.length < 3) return;
    setSearchStatus("searching");

    api
      .get(`/api/a/publisher/searchTerm/${searchTerm}?status=active`)
      .then((response) => {
        const found = response.data.data || null;

        if (!found) {
          setPublishers([]);
          setSelectedPublisher({});
          setSearchStatus("stopped");
          setPrevSearched(true);
          return;
        }

        setPublishers(found);

        setSearchStatus("stopped");
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err?.response?.data?.message || "Error fetching publisher",
        });
      })
      .finally(() => {
        setSearchStatus("idle");
        setPrevSearched(true);
      });
  }

  async function sendCampaignUpdateEmail(campaignId, publisherId) {
    if (mailingCampInfo) return;
    setMailingCampInfo(true);

    try {
      const res = await api.post("/api/a/publisher/send-campaign-update", {
        campaignId,
        publisherId,
      });

      const data = res.data;

      if (!data.status) {
        showToast({
          status: "error",
          message: data.message || "Failed to send campaign update email",
        });
        setMailingCampInfo(false);
        return false;
      }

      showToast({
        status: "success",
        message: "Campaign details email sent successfully",
      });

      setMailingCampInfo(false);
      return true;

    } catch (error) {
      showToast({
        status: "error",
        message:
          error.response?.data?.message ||
          "Internal error. Please try again.",
      });

      setMailingCampInfo(false);
      return false;
    }
  }

  return (
    <div className="campaigns__page">
      <AdminSideNav
        userName={userInfo.name}
        emailID={userInfo.email}
        active={isSideNavActive}
      />
      <div
        onClick={() => setisSideNavActive(!isSideNavActive)}
        className={
          isSideNavActive ? "background-shadow active" : "background-shadow"
        }
      ></div>
      <main>
        <header className="desktop-header">
          <div>
            <h2>Campaigns</h2>
            <p>You can checkout out campaigns from here.</p>
          </div>
          <button onClick={() => {
            if (isNotAdminUser) return showToast({
              status: "error",
              message: "You are not authorized to perform this action."
            }); navigate("/admin/campaigns/create")
          }}>
            Create <Icon name="PlusCircle" className="onRight" />
          </button>
        </header>

        <header className="mobile-header">
          <div>
            <h2>Campaigns</h2>
          </div>
          <button
            className="nav-btn"
            onClick={() => setisSideNavActive(!isSideNavActive)}
          >
            {isSideNavActive ? (
              <Icon name="XMark" className="onLeft" />
            ) : (
              <Icon name="Bars3" className="onLeft" />
            )}
          </button>
        </header>

        <div className="search-box">
          <Icon name="MagnifyingGlass" />
          <input
            type="text"
            placeholder="Search for campaigns by name eg: Swift Digital Ads"
            autoComplete="off"
            onChange={handelCampSearchInpChnage}
            value={campaignSearchText}
          />
        </div>

        <div className="table-container">
          <div className="top-section">
            <div>Name</div>
            <div>Type</div>
            <div>Payout</div>
            <div>Category</div>
            <div>Location</div>
            <div>Status</div>
            <div>Action</div>
          </div>

          {campaignsFetchStatus === "failed" ? (
            <div className="flat-message">
              <Icon name="ExclamationCircle" className="msg-icon" />
              <h3 className="title">Campaigns Retrieval Failed</h3>
              <p className="description">
                We encountered an error while trying to fetch campaigns. Please
                try again later.
              </p>
            </div>
          ) : campaigns && campaigns.length > 0 && !isSearchingCamps ? (
            campaigns.map((campaign, index) => {
              return (
                <div className="datas" key={campaign.campaignId}>
                  <div className="data campaign-name">
                    <img
                      src={
                        campaign.logo ? campaign.logo : "../../DefaultImage.png"
                      }
                      alt="logo"
                      className="logo"
                    />
                    <div>
                      <span>{campaign.name}</span>
                      <span>{campaign.campaignId}</span>
                    </div>
                  </div>
                  <div className="data">{campaign.type}</div>
                  <div className="data">
                    {convertCurrency(
                      campaign?.goals
                        ?.filter((goal) => goal.isMain === true)
                        ?.map((goal) => goal.value)
                        .join(", "),
                      campaign?.goals
                        ?.filter((goal) => goal.isMain === true)
                        ?.map((goal) => goal.currency)
                        .join(", "),
                      currency
                    ) || "₹0"}
                  </div>
                  <div className="data">{campaign.category}</div>
                  <div className="data">
                    <div className={`cn-container ${campaign.allowed_locations.length === 1 ? "" : "multiple"}`}>
                      {
                        campaign.allowed_locations.map((location, index) => {
                          if (index >= 4) return null;
                          return (
                            <div className={index < 3 ? "cn" : "more-cn"} key={location}>
                              {index < 3 ? campaign.allowed_locations.length === 1 ? getLabel(location) : getFlag(location) : ""}
                              {index === 3 && campaign.allowed_locations.length > 3 ? `+${campaign.allowed_locations.length - 3}` : null}
                            </div>
                          )
                        })
                      }
                    </div>
                  </div>
                  <div className="data">
                    <span
                      className={`status ${campaign.status}`}
                      style={{ cursor: "pointer" }}
                      disabled={isUpdatingStatus[campaign.campaignId]}
                      onClick={() => handleToggleStatus(campaign)}>
                      {isUpdatingStatus[campaign.campaignId]
                        ? "Updating..."
                        : campaign.status === "active"
                          ? "Live"
                          : "Paused"}
                      {isUpdatingStatus[campaign.campaignId] && <Icon name="ArrowPath" className="spin" />}
                    </span>
                  </div>
                  <div className="data a-actions icons-only">
                    <button onClick={() => handelActionButton(campaign)}>
                      <Icon name="PencilSquare" />
                    </button>
                    <button onClick={() => { setGetTrackingLinkWindow(true); setSelectedCampaign(campaign) }}>
                      <Icon name="EllipsisHorizontal" />
                    </button>
                  </div>
                </div>
              );
            })
          ) : campaignsFetchStatus !== "fetching" &&
            campaignsFetchStatus !== "fetching_first" &&
            !isSearchingCamps ? (
            <div className="flat-message">
              <Icon name="ListBullet" className="msg-icon" />
              <h3 className="title">No Campaign Available</h3>
              <p className="description">
                Opps! Seems like we dont have any campaign at this moment.
              </p>
            </div>
          ) : (
            ""
          )}
        </div>
        {campaignsFetchStatus === "fetching" ||
          campaignsFetchStatus === "fetching_first" ||
          isSearchingCamps ? (
          <span className="bottom-fetch">
            <Icon name="ArrowPath" />{" "}
            {isSearchingCamps
              ? "Searching..."
              : campaignsFetchStatus === "fetching_first"
                ? "Fetching..."
                : "Fetching more..."}
          </span>
        ) : (
          ""
        )}
        <div className={`publisher-selector-modal modal-overlay ${getTrackingLinkWindow ? "active" : ""}`} onClick={() => setGetTrackingLinkWindow(null)}></div>
        <div className={`publisher-selector-modal modal-container ${getTrackingLinkWindow ? "active" : ""}`}>

          <div className="modal-header">
            <h2 className="modal-title">More Action</h2>
            <button className="close-btn" onClick={() => setGetTrackingLinkWindow(null)}><Icon name="XMark" /></button>
          </div>

          <div className="modal-search">
            <div className="search-wrap">
              <input
                className="search-input"
                placeholder="Search publisher by name or email..."
                type="email"
                value={pubEmailToSearch}
                onChange={(e) => handleSearchTyping(e.target.value)}
              />

              <span className="search-icon">
                {searchStatus === "searching" ? (
                  <Icon name="ArrowPath" className="spin" />
                ) : (
                  <Icon name="MagnifyingGlass" />
                )}
              </span>
            </div>
          </div>

          <div className="modal-body">

            <div className="publisher-list">
              {pubFetchStatus === "fetching" || pubFetchStatus === "fetching_first" || searchStatus === "searching" ? (
                <>
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="publisher-item loading">
                      <div className="avatar skeleton"></div>
                      <div className="item-info">
                        <div className="item-name skeleton"></div>
                        <div className="item-email skeleton"></div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                publishers?.length > 0 &&
                publishers.map((p) => (
                  <div
                    key={p.publisherId}
                    className={`publisher-item ${p.publisherId === selectedPublisher?.publisherId
                      ? "publisher-item-selected"
                      : ""
                      }`}
                    onClick={() => setSelectedPublisher(p)}
                  >
                    <div className="avatar">{p.name?.[0]}</div>

                    <div className="item-info">
                      <div className="item-name">{p.name}</div>
                      <div className="item-email">{p.email}</div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="right-panel">
              {selectedPublisher && (
                <>
                  <div className="detail-card">
                    <div className="detail-title">Publisher</div>
                    <div className="detail-name">
                      {selectedPublisher?.name || "Not Selected"}
                    </div>
                    <div className="detail-email">{selectedPublisher.email}</div>
                    <span className={`detail-status ${selectedPublisher?.campaignsJoined?.includes?.(selectedCampaign._id) ? "approved" : "not-approved"}`}>
                      {selectedPublisher?.name ? selectedPublisher?.campaignsJoined?.includes?.(selectedCampaign._id) ? "Joined in campaign" : "Not Approved for this campaign" : ""}
                    </span>
                    <div className="detail-track-heading">Tracking Link</div>

                    {selectedPublisher?.campaignsJoined?.includes?.(selectedCampaign._id) ? (
                      <a
                        className="detail-track-link"
                        href={`${window.location.origin === "http://localhost:3000"
                          ? "http://localhost:5500"
                          : window.location.origin
                          }/track/?pubID=${selectedPublisher.publisherId}&campID=${selectedCampaign.campaignId}&aff_click_id={aff_click_id}&sub_aff_id={sub_aff_id}&source={source}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {`${window.location.origin === "http://localhost:3000"
                          ? "http://localhost:5500"
                          : window.location.origin
                          }/track/?pubID=${selectedPublisher.publisherId}&campID=${selectedCampaign.campaignId}&aff_click_id={aff_click_id}&sub_aff_id={sub_aff_id}&source={source}`}
                      </a>
                    ) : (
                      <div className="detail-no-link">No tracking link</div>
                    )}
                  </div>
                  {selectedPublisher?.name && (
                    <div className="actions">
                      {!selectedPublisher?.campaignsJoined?.includes(selectedCampaign._id) ? (
                        <button
                          disabled={gettingLink}
                          onClick={() =>
                            joinPublisherToCampaign(
                              selectedCampaign.campaignId,
                              selectedPublisher.publisherId
                            )
                          }
                          className="btn-get-link"
                        >
                          {gettingLink ? "Please wait..." : "Approve"}
                        </button>
                      ) : (
                        <button
                          disabled={gettingLink}
                          onClick={() =>
                            rejectPublisherFromCampaign(
                              selectedCampaign.campaignId,
                              selectedPublisher.publisherId
                            )
                          }
                          className="btn-reject"
                        >
                          {gettingLink ? "Please wait..." : "Reject"}
                        </button>
                      )}
                      <button
                        disabled={mailingCampInfo}
                        onClick={() =>
                          sendCampaignUpdateEmail(
                            selectedCampaign.campaignId,
                            selectedPublisher.publisherId
                          )
                        }
                        className="btn-normal"
                      >
                        {mailingCampInfo ? "Please wait..." : "Send Campaign Info Email"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

          </div>
        </div>

        <span className="copyright-text">
          © 2025 Swift Digital Ads, All rights reserved
        </span>
      </main>
    </div>
  );
};

export default AdminCampaignsPage;
